import LocaleChipSelect from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal/LocaleChipSelect";
import {
    getFlagByLocale,
    RESOURCE_NAME,
    SupportedLocales,
} from "@app/shared/utils/locales";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { TagMultiSelect } from "@design-system/Inputs/TagMultiSelect";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { type FC, type PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useCreateSupplierTemplate,
    useUpdateSupplierTemplate,
} from "../../data";
import { useCreateQuestionnaireModalTranslations } from "./i18n";

export const QuestionnaireModal: FC<
    PropsWithChildren<{
        open: boolean;
        onOpenChange: (open: boolean) => void;
        onConfirm: (questionnaireInput: {
            availableLocales: SupportedLocales[];
            default: SupportedLocales;
        }) => void;
        questionnaire?: {
            availableLocales: SupportedLocales[];
            defaultLocale: SupportedLocales;
        };
    }>
> = ({ children, open, onOpenChange, onConfirm, questionnaire }) => {
    const availableLocales = Object.values(SupportedLocales);
    const [selectedLocales, setSelectedLocales] = useState<
        SupportedLocales[] | undefined
    >(questionnaire?.availableLocales ?? [SupportedLocales.EN]);
    const filteredLocales = availableLocales.filter(
        (locale) => !selectedLocales?.includes(locale),
    );

    const [defaultLocale, setDefaultLocale] = useState<
        SupportedLocales | undefined
    >(questionnaire?.defaultLocale ?? SupportedLocales.EN);

    const handleSelectedLocalesChange = (value: string | string[] | null) => {
        if (!value || !Array.isArray(value)) return;

        if (!defaultLocale) {
            setDefaultLocale(value[0] as SupportedLocales);
        }

        if (defaultLocale && !value.includes(defaultLocale)) {
            setDefaultLocale(value[0] as SupportedLocales);
        }
        setSelectedLocales(value as SupportedLocales[]);
    };

    const unselectLocale = (locale: SupportedLocales) => {
        return () => {
            if (selectedLocales?.length === 1) {
                setDefaultLocale(undefined);
            }

            if (defaultLocale === locale) {
                setDefaultLocale(selectedLocales?.find((l) => l !== locale));
            }

            setSelectedLocales((prev) => {
                if (!prev) return undefined;
                return prev.filter((l) => l !== locale);
            });
        };
    };

    const handleConfirmClick = () => {
        if (!selectedLocales?.length || !defaultLocale) return;
        onConfirm({
            availableLocales: selectedLocales,
            default: defaultLocale,
        });
    };

    const { t: tLocale } = useTranslation(RESOURCE_NAME);
    const { t, keys } = useCreateQuestionnaireModalTranslations();

    return (
        <Modal open={open} onOpenChange={onOpenChange}>
            <Modal.Trigger asChild>
                {children ?? <Button>{t(keys.empty_state_button)}</Button>}
            </Modal.Trigger>
            <Modal.Overlay scrollable>
                <Modal.Content>
                    <Modal.Header>
                        <Modal.Title>{t(keys.title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <FlexCol gap="2">
                                <Text color="primary">
                                    {t(keys.select_languages)}
                                </Text>
                                <FlexCol>
                                    <TagMultiSelect
                                        multiple
                                        value={selectedLocales}
                                        onChange={handleSelectedLocalesChange}
                                    >
                                        <TagMultiSelect.Trigger>
                                            <FlexRow gap="2" w="full">
                                                {selectedLocales?.map(
                                                    (locale) => (
                                                        <TagMultiSelect.Tag
                                                            key={locale}
                                                            size="sm"
                                                            onClick={unselectLocale(
                                                                locale,
                                                            )}
                                                        >
                                                            <Icon
                                                                className="text-primary"
                                                                name={getFlagByLocale(
                                                                    locale,
                                                                )}
                                                                size="xs"
                                                            />
                                                            {tLocale(locale)}
                                                            <Icon
                                                                name="close"
                                                                size="xs"
                                                            />
                                                        </TagMultiSelect.Tag>
                                                    ),
                                                )}
                                                <TagMultiSelect.Button className="flex-1">
                                                    <FlexRow
                                                        w="full"
                                                        gap="2"
                                                        alignItems="center"
                                                        justifyContent="end"
                                                    >
                                                        <Text
                                                            variant="body-sm"
                                                            color="secondary"
                                                        >
                                                            {t(
                                                                keys.select_locales,
                                                            )}
                                                        </Text>
                                                        <Icon
                                                            className="text-primary"
                                                            name="chevronDown"
                                                            size="sm"
                                                        />
                                                    </FlexRow>
                                                </TagMultiSelect.Button>
                                            </FlexRow>
                                        </TagMultiSelect.Trigger>
                                        <TagMultiSelect.Options className="border empty:invisible">
                                            {filteredLocales.map((locale) => (
                                                <TagMultiSelect.Option
                                                    key={locale}
                                                    value={locale}
                                                >
                                                    {tLocale(locale)}
                                                </TagMultiSelect.Option>
                                            ))}
                                        </TagMultiSelect.Options>
                                    </TagMultiSelect>
                                </FlexCol>
                            </FlexCol>
                            {!!defaultLocale && !!selectedLocales?.length && (
                                <FlexCol gap="2">
                                    <Text color="primary">
                                        {t(keys.default_language)}
                                    </Text>
                                    <LocaleChipSelect
                                        locale={defaultLocale}
                                        setLocale={setDefaultLocale}
                                        availableLocales={selectedLocales}
                                    />
                                </FlexCol>
                            )}
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>{t(keys.cancel)}</Modal.CancelCTA>
                        <Modal.ConfirmCTA
                            disabled={!selectedLocales?.length}
                            onClick={handleConfirmClick}
                        >
                            {questionnaire ? t(keys.edit) : t(keys.create)}
                        </Modal.ConfirmCTA>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export const CreateQuestionnaireModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
    const { createSupplierTemplate } = useCreateSupplierTemplate();

    return (
        <QuestionnaireModal
            open={open}
            onOpenChange={onOpenChange}
            onConfirm={createSupplierTemplate}
        />
    );
};

export const UpdateQuestionnaireModal: FC<{
    questionnaire: {
        availableLocales: SupportedLocales[];
        defaultLocale: SupportedLocales;
    };
}> = ({ questionnaire }) => {
    const [open, setOpen] = useState(false);
    const { updateSupplierTemplate } = useUpdateSupplierTemplate();

    return (
        <QuestionnaireModal
            open={open}
            onOpenChange={setOpen}
            onConfirm={updateSupplierTemplate}
            questionnaire={questionnaire}
        >
            <Button className="h-7" variant="outlined" size="sm">
                <Button.Icon name="edit" />
            </Button>
        </QuestionnaireModal>
    );
};
